import React, { useEffect, useMemo, useState, useRef } from 'react';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Box,
  IconButton,
  Checkbox,
  TextField,
  Alert,
  ListItem,
  List,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Chip,
  Stack,
  FormControl,
  InputLabel,
  Select,
  SelectChangeEvent,
  Tooltip,
  Divider,
} from '@mui/material';
import FormControlLabel from '@mui/material/FormControlLabel';
import {
  ExpandMore as ExpandMoreIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  InfoOutlined as InfoOutlinedIcon,
  Check as CheckIcon,
  DriveFileMove as MoveIcon,
  ArrowRight as ArrowRightIcon,
  ExpandLess as ExpandLessIcon,
  Sort as SortIcon,
  MoreVert as MoreVertIcon,
} from '@mui/icons-material';
import { Droppable, Draggable } from '@hello-pangea/dnd';
import { ChecklistItem, ChecklistType, PasoTags, PriorityLevel } from '../types/api';
import ColorSwatchModal from './ColorSwatchModal';
import MarkdownText from './MarkdownText';
import ContextMenu, { ContextMenuPosition } from './ContextMenu';
import GenericModal from './GenericModal';
import SaveActionButton from './SaveActionButton';
import { DueDateChipLabel, NullSelectSymbol, PriorityChipLabel, PriorityMenuOption, PrioritySelectValue, TimeChipLabel, UnknownPriorityChipLabel } from './PasoTagVisuals';
import { AIRMASK_GREEN } from '../constants/colors';
import { buildGroupedTagModel, flattenGroupedTagModel } from '../utils/tagGrouping';
import { buildTagsFromEditor, toTagEditorState } from '../utils/pasoTags';
import { groupWsjfItems, hasUnknownWsjfPriority, PasoSortMode, sortItemsByTimeOrder } from '../utils/pasoSorting';
import { useIsCoarsePointer } from '../hooks/useMobileKeyboardVisible';

interface ChecklistExpandProps {
  title: string;
  checklistType: ChecklistType;
  items: ChecklistItem[];
  expanded: boolean;
  onExpandChange: (expanded: boolean) => void;
  onAddItem: (text: string, options?: { successMessage?: string | null }) => Promise<boolean | void>;
  onCheckItem: (itemId: string, checked: boolean) => void;
  onDeleteItem: (itemId: string) => void;
  onEditItem: (itemId: string, newText: string, options?: { tags?: PasoTags | null }) => void;
  onColorChange: (itemId: string, color: string) => void;
  onMoveItem: (itemId: string, targetChecklistType: ChecklistType) => void;
  allChecklistConfigs: ReadonlyArray<{ type: ChecklistType; title: string }>;
  onShowAllPasos?: (checklistTitle: string) => void;
}

const LONG_PRESS_DURATION_MS = 500;
const LONG_PRESS_MOVE_TOLERANCE_PX = 10;

const ChecklistExpand: React.FC<ChecklistExpandProps> = ({
  title,
  checklistType,
  items,
  expanded,
  onExpandChange,
  onAddItem,
  onCheckItem,
  onDeleteItem,
  onEditItem,
  onColorChange,
  onMoveItem,
  allChecklistConfigs,
  onShowAllPasos,
}) => {
  const [newItemText, setNewItemText] = useState('');
  const [showAddInput, setShowAddInput] = useState(false);
  const [isMultiAddMode, setIsMultiAddMode] = useState(false);
  const [showMultiAddHint, setShowMultiAddHint] = useState(false);
  const [editingItemId, setEditingItemId] = useState<string | null>(null);
  const [editText, setEditText] = useState('');
  const [colorModalAnchor, setColorModalAnchor] = useState<HTMLElement | null>(null);
  const [selectedItemId, setSelectedItemId] = useState<string | null>(null);
  const [selectedItemColor, setSelectedItemColor] = useState<string | undefined>(undefined);
  const [sortMode, setSortMode] = useState<PasoSortMode>('manual');
  const [sortMenuAnchor, setSortMenuAnchor] = useState<HTMLElement | null>(null);
  const [expandedGroupIds, setExpandedGroupIds] = useState<Set<string>>(new Set());
  const [showAllItems, setShowAllItems] = useState(false);

  const [tagCategoriesInput, setTagCategoriesInput] = useState('');
  const [isTagCategoriesFocused, setIsTagCategoriesFocused] = useState(false);
  const [dueDateValue, setDueDateValue] = useState('');
  const [isHuge, setIsHuge] = useState(false);
  const [estimatedTimeValue, setEstimatedTimeValue] = useState('');
  const [estimatedTimeUnit, setEstimatedTimeUnit] = useState<'' | 'minutes' | 'hours'>('minutes');
  const [opportunityCostIfNotDone, setOpportunityCostIfNotDone] = useState<'' | 'high' | 'moderate' | 'low'>('');
  const [valueIfDone, setValueIfDone] = useState<'' | 'high' | 'moderate' | 'low'>('');

  // Context menu state
  const [contextMenu, setContextMenu] = useState<ContextMenuPosition | null>(null);
  const [contextMenuItemId, setContextMenuItemId] = useState<string | null>(null);
  const [moveSubmenuAnchor, setMoveSubmenuAnchor] = useState<HTMLElement | null>(null);
  const isCoarsePointer = useIsCoarsePointer();

  // Ref to the droppable list, used to constrain drag within list bounds
  const listRef = useRef<HTMLElement>(null);
  const editFormRef = useRef<HTMLDivElement>(null);
  const addFormRef = useRef<HTMLDivElement>(null);
  const longPressTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const touchStartRef = useRef<{ x: number; y: number } | null>(null);
  const suppressNextItemClickRef = useRef(false);

  const scrollToChecklistBottom = () => {
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        const listElement = listRef.current;
        if (!listElement) {
          return;
        }

        const lastPasoRow = listElement.querySelector<HTMLElement>('[data-paso-item-row="true"]:last-of-type');
        if (lastPasoRow) {
          lastPasoRow.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
      });
    });
  };

  const handleAddItem = () => {
    if (newItemText.trim()) {
      const itemsToAdd = isMultiAddMode
        ? newItemText
          .split(/\n\s*\n+/)
          .map(text => text.trim())
          .filter(Boolean)
        : [newItemText.trim()];

      const hadOverflowDots = activeItems.length > 10 && !showAllItems;
      const addRequests: Array<Promise<boolean | void>> = [];

      for (let index = 0; index < itemsToAdd.length; index += 1) {
        const isLastItemInBatch = index === itemsToAdd.length - 1;
        const successMessage = hadOverflowDots
          ? `${isMultiAddMode ? 'Pasos' : 'Paso'} added successfully. Showing all Pasos in ${title}`
          : (isMultiAddMode && isLastItemInBatch ? 'Pasos added successfully' : undefined);

        if (isMultiAddMode) {
          const addRequest = onAddItem(itemsToAdd[index], {
            successMessage: isLastItemInBatch ? successMessage : null,
          });
          addRequests.push(addRequest);
        } else {
          const addRequest = successMessage === undefined
            ? onAddItem(itemsToAdd[index])
            : onAddItem(itemsToAdd[index], { successMessage });
          addRequests.push(addRequest);
        }
      }

      setNewItemText('');
      setShowAddInput(false);
      setIsMultiAddMode(false);

      if (hadOverflowDots) {
        void Promise.all(addRequests).then((results) => {
          const anyAddSucceeded = results.some((result) => result !== false);
          if (anyAddSucceeded) {
            setShowAllItems(true);
            scrollToChecklistBottom();
          }
        });
      } else {
        void Promise.all(addRequests).then((results) => {
          const anyAddSucceeded = results.some((result) => result !== false);
          if (anyAddSucceeded) {
            scrollToChecklistBottom();
          }
        });
      }
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      if (isCoarsePointer) {
        return;
      }

      if (!e.shiftKey) {
        // Plain Enter saves the item
        e.preventDefault();
        void handleAddItem();
      }
      // Shift+Enter: allow default behavior (insert newline)
    } else if (e.key === 'Escape') {
      setNewItemText('');
      setShowAddInput(false);
      setIsMultiAddMode(false);
    }
  };

  const handleAddButtonHover = (event: React.MouseEvent<HTMLElement>) => {
    setShowMultiAddHint(event.metaKey || event.ctrlKey);
  };

  const handleOpenAddInput = (event: React.MouseEvent<HTMLElement>) => {
    setIsMultiAddMode(event.metaKey || event.ctrlKey);
    setShowAddInput(true);
  };

  const handleStartEdit = (itemId: string, currentText: string) => {
    setEditingItemId(itemId);
    setEditText(currentText);

    const item = items.find(i => i.id === itemId);
    const tagState = toTagEditorState(item?.tags);
    setTagCategoriesInput(tagState.categoriesInput);
    setIsTagCategoriesFocused(false);
    setDueDateValue(tagState.dueDate);
    setIsHuge(tagState.isHuge);
    setEstimatedTimeValue(tagState.estimatedTimeValue);
    setEstimatedTimeUnit(tagState.estimatedTimeUnit);
    setOpportunityCostIfNotDone(tagState.opportunityCostIfNotDone);
    setValueIfDone(tagState.valueIfDone);
  };

  const handleSaveEdit = (itemId: string, options?: { preserveEditorOnNoChange?: boolean }) => {
    const currentItem = items.find(item => item.id === itemId);
    if (!currentItem) {
      setEditingItemId(null);
      setEditText('');
      return;
    }

    const tags = buildTagsFromEditor({
      enabled: true,
      categoriesInput: tagCategoriesInput,
      dueDate: dueDateValue,
      isHuge,
      estimatedTimeValue,
      estimatedTimeUnit,
      opportunityCostIfNotDone,
      valueIfDone,
    });

    const changedText = editText.trim() && editText.trim() !== currentItem.text;
    const changedTags = JSON.stringify(currentItem.tags || null) !== JSON.stringify(tags);

    if (changedText || changedTags) {
      if (changedTags) {
        onEditItem(itemId, changedText ? editText.trim() : currentItem.text, { tags });
      } else {
        onEditItem(itemId, editText.trim());
      }
    } else if (options?.preserveEditorOnNoChange) {
      return;
    }
    setEditingItemId(null);
    setEditText('');
    setIsTagCategoriesFocused(false);
    setDueDateValue('');
    setIsHuge(false);
  };

  const handleCancelEdit = () => {
    setEditingItemId(null);
    setEditText('');
    setTagCategoriesInput('');
    setIsTagCategoriesFocused(false);
    setDueDateValue('');
    setIsHuge(false);
    setEstimatedTimeValue('');
    setEstimatedTimeUnit('minutes');
    setOpportunityCostIfNotDone('');
    setValueIfDone('');
  };

  const handleEditTextFieldBlur = (event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>, itemId: string) => {
    if (!isCoarsePointer) {
      return;
    }

    const relatedTarget = event.relatedTarget as HTMLElement | null;
    if (relatedTarget && editFormRef.current?.contains(relatedTarget)) {
      return;
    }

    window.requestAnimationFrame(() => {
      handleSaveEdit(itemId, { preserveEditorOnNoChange: true });
    });
  };

  const handleAddTextFieldBlur = (event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (!isCoarsePointer) {
      return;
    }

    const relatedTarget = event.relatedTarget as HTMLElement | null;
    if (relatedTarget && addFormRef.current?.contains(relatedTarget)) {
      return;
    }

    window.requestAnimationFrame(() => {
      void handleAddItem();
    });
  };

  const handleEditKeyPress = (e: React.KeyboardEvent, itemId: string) => {
    if (e.key === 'Enter' && !isCoarsePointer) {
      if (!e.shiftKey) {
        // Plain Enter saves the edit
        e.preventDefault();
        handleSaveEdit(itemId);
      }
      // Shift+Enter: allow default behavior (insert newline)
    } else if (e.key === 'Escape') {
      handleCancelEdit();
    }
  };

  const handleItemClick = (event: React.MouseEvent<HTMLElement>, itemId: string, itemColor: string | undefined) => {
    if (suppressNextItemClickRef.current) {
      suppressNextItemClickRef.current = false;
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    if (editingItemId !== itemId) {
      setColorModalAnchor(event.currentTarget);
      setSelectedItemId(itemId);
      setSelectedItemColor(itemColor);
    }
  };

  const handleColorSelect = (color: string) => {
    if (selectedItemId) {
      onColorChange(selectedItemId, color);
    }
    setColorModalAnchor(null);
    setSelectedItemId(null);
    setSelectedItemColor(undefined);
  };

  const handleCloseColorModal = () => {
    setColorModalAnchor(null);
    setSelectedItemId(null);
    setSelectedItemColor(undefined);
  };

  const getItemBackgroundColor = (customColor?: string) => {
    if (customColor) {
      return customColor;
    }
    return 'background.paper';
  };

  type RenderEntry = {
    kind: 'group' | 'item';
    id: string;
    depth: number;
    groupedContext: boolean;
    isExpandableGroup?: boolean;
    expanded?: boolean;
    label?: string;
    itemCount?: number;
    wsjfCost?: PriorityLevel | null;
    wsjfValue?: PriorityLevel | null;
    item?: ChecklistItem;
  };

  const parseWsjfPriority = (raw: string): PriorityLevel | null => {
    if (raw === 'high' || raw === 'moderate' || raw === 'low') {
      return raw;
    }
    return null;
  };

  const activeItems = useMemo(() => items.filter(item => !item.done), [items]);
  const groupedModel = useMemo(
    () => (sortMode === 'manual' ? buildGroupedTagModel(activeItems) : null),
    [activeItems, sortMode]
  );
  const flatGroupedEntries = useMemo(
    () => (groupedModel ? flattenGroupedTagModel(groupedModel, activeItems, expandedGroupIds) : []),
    [groupedModel, activeItems, expandedGroupIds]
  );
  const wsjfGroups = useMemo(
    () => (sortMode === 'wsjf' ? groupWsjfItems(activeItems) : []),
    [activeItems, sortMode]
  );
  const timeSortedItems = useMemo(
    () => (sortMode === 'time' ? sortItemsByTimeOrder(activeItems) : []),
    [activeItems, sortMode]
  );
  const renderEntries = useMemo<RenderEntry[]>(() => {
    if (sortMode === 'manual') {
      return flatGroupedEntries.map((entry) => {
        if (entry.kind === 'group' && entry.group) {
          return {
            kind: 'group',
            id: entry.group.id,
            depth: entry.depth,
            groupedContext: false,
            isExpandableGroup: true,
            expanded: expandedGroupIds.has(entry.group.id),
            label: entry.group.displayPath.replace(/:/g, ' > '),
            itemCount: entry.group.itemIds.length,
          };
        }

        return {
          kind: 'item',
          id: entry.item!.id,
          depth: entry.depth,
          groupedContext: entry.groupedContext,
          item: entry.item,
        };
      });
    }

    if (sortMode === 'time') {
      return timeSortedItems.map(item => ({
        kind: 'item',
        id: item.id,
        depth: 0,
        groupedContext: false,
        item,
      }));
    }

    const rows: RenderEntry[] = [];
    wsjfGroups.forEach(group => {
      const groupId = `wsjf-${group.key}`;
      const isExpanded = expandedGroupIds.has(groupId);
      const [rawCost, rawValue] = group.key.split('|');
      const wsjfCost = parseWsjfPriority(rawCost || '');
      const wsjfValue = parseWsjfPriority(rawValue || '');
      rows.push({
        kind: 'group',
        id: groupId,
        depth: 0,
        groupedContext: false,
        isExpandableGroup: true,
        expanded: isExpanded,
        label: group.label,
        itemCount: group.items.length,
        wsjfCost,
        wsjfValue,
      });

      if (isExpanded) {
        group.items.forEach(item => {
          rows.push({
            kind: 'item',
            id: item.id,
            depth: 1,
            groupedContext: true,
            item,
          });
        });
      }
    });

    return rows;
  }, [sortMode, flatGroupedEntries, expandedGroupIds, timeSortedItems, wsjfGroups]);

  const visualCount = activeItems.length;
  const showWarning = activeItems.length >= 10;
  const shouldShowOverflowDots = expanded && activeItems.length > 10 && !showAllItems;

  const visibleRenderEntries = useMemo(() => {
    if (!shouldShowOverflowDots) {
      return renderEntries;
    }

    const limitedEntries: RenderEntry[] = [];
    let visibleItemCount = 0;

    for (const entry of renderEntries) {
      if (entry.kind === 'item') {
        if (visibleItemCount >= 10) {
          break;
        }
        visibleItemCount += 1;
      }

      limitedEntries.push(entry);
    }

    return limitedEntries;
  }, [renderEntries, shouldShowOverflowDots]);

  const toggleGroupExpansion = (groupId: string) => {
    setExpandedGroupIds(prev => {
      const next = new Set(prev);
      if (next.has(groupId)) {
        next.delete(groupId);
      } else {
        next.add(groupId);
      }
      return next;
    });
  };

  const handleOpenSortMenu = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setSortMenuAnchor(event.currentTarget);
  };

  const handleCloseSortMenu = () => {
    setSortMenuAnchor(null);
  };

  const handleSelectSortMode = (mode: PasoSortMode) => {
    setSortMode(mode);
    setSortMenuAnchor(null);
  };

  useEffect(() => {
    setExpandedGroupIds(new Set());
  }, [sortMode]);

  useEffect(() => {
    if (expanded) {
      setShowAllItems(false);
    }
  }, [expanded]);

  const getCategoryChips = (item: ChecklistItem, groupedContext: boolean): string[] => {
    if (groupedContext) return [];
    return (item.tags?.categories || []).slice(0, 5);
  };

  // Helper to check if item is multi-line
  const isMultiLine = (text: string) => text.includes('\n');

  // Dense mode: always show full text content for PASOS items.
  const getDisplayText = (text: string) => text;

  const currentEditingItem = useMemo(
    () => (editingItemId ? items.find((item) => item.id === editingItemId) ?? null : null),
    [editingItemId, items]
  );

  // Constrain dragged item to stay within the list boundaries.
  // The library uses position:fixed + transform:translate() to move the item,
  // so we clamp the transform to keep the item within the list's viewport rect.
  const getConstrainedDragStyle = (
    draggableStyle: React.CSSProperties | undefined,
    isDragging: boolean
  ): React.CSSProperties | undefined => {
    if (!isDragging || !draggableStyle || !listRef.current) {
      return draggableStyle;
    }

    const listRect = listRef.current.getBoundingClientRect();
    const itemHeight = (draggableStyle.height as number) || 0;
    const itemTop = draggableStyle.top as number;

    // Parse the transform to extract translateX and translateY
    const transform = (draggableStyle as any).transform as string || '';
    const match = transform.match(/translate\(([^,]+),\s*([^)]+)\)/);
    const translateX = match ? match[1] : '0px';
    const translateY = match ? parseFloat(match[2]) : 0;

    // Current visual top = fixed top + translateY
    const visualTop = itemTop + translateY;

    // Clamp: stick at list top edge, and list bottom edge minus item height
    const minTop = listRect.top;
    const maxTop = listRect.bottom - itemHeight;
    const clampedVisualTop = Math.max(minTop, Math.min(visualTop, maxTop));

    // Adjust translateY to achieve clamped position
    const clampedTranslateY = clampedVisualTop - itemTop;

    return {
      ...draggableStyle,
      transform: `translate(${translateX}, ${clampedTranslateY}px)`,
    };
  };

  // Context menu handlers
  const handleContextMenu = (event: React.MouseEvent, itemId: string) => {
    event.preventDefault();
    event.stopPropagation();
    setContextMenu({ mouseX: event.clientX - 2, mouseY: event.clientY - 4 });
    setContextMenuItemId(itemId);
  };

  const handleItemTouchStart = (event: React.TouchEvent<HTMLElement>, itemId: string) => {
    if (event.touches.length !== 1) {
      touchStartRef.current = null;
      if (longPressTimerRef.current) {
        clearTimeout(longPressTimerRef.current);
        longPressTimerRef.current = null;
      }
      return;
    }

    const touch = event.touches[0];
    touchStartRef.current = { x: touch.clientX, y: touch.clientY };

    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
    }

    longPressTimerRef.current = setTimeout(() => {
      suppressNextItemClickRef.current = true;
      setContextMenu({ mouseX: touch.clientX - 2, mouseY: touch.clientY - 4 });
      setContextMenuItemId(itemId);
      longPressTimerRef.current = null;
    }, LONG_PRESS_DURATION_MS);
  };

  const handleItemTouchMove = (event: React.TouchEvent<HTMLElement>) => {
    if (!touchStartRef.current || event.touches.length !== 1) {
      return;
    }

    const touch = event.touches[0];
    const deltaX = Math.abs(touch.clientX - touchStartRef.current.x);
    const deltaY = Math.abs(touch.clientY - touchStartRef.current.y);
    if (deltaX <= LONG_PRESS_MOVE_TOLERANCE_PX && deltaY <= LONG_PRESS_MOVE_TOLERANCE_PX) {
      return;
    }

    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
    touchStartRef.current = null;
  };

  const clearLongPressState = () => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
    touchStartRef.current = null;
  };

  const handleCloseContextMenu = () => {
    setContextMenu(null);
    setContextMenuItemId(null);
    setMoveSubmenuAnchor(null);
  };

  const handleMoveClick = (event: React.MouseEvent<HTMLElement>) => {
    setMoveSubmenuAnchor(event.currentTarget);
  };

  const handleMoveToChecklist = (targetType: ChecklistType) => {
    if (contextMenuItemId) {
      onMoveItem(contextMenuItemId, targetType);
    }
    handleCloseContextMenu();
  };

  useEffect(() => {
    return () => {
      if (longPressTimerRef.current) {
        clearTimeout(longPressTimerRef.current);
      }
    };
  }, []);

  return (
    <Accordion
      expanded={expanded}
      onChange={(_, isExpanded) => onExpandChange(isExpanded)}
      sx={{ mb: 1 }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
          <Typography variant="h6">
            {title} {visualCount > 0 && `(${visualCount})`}
          </Typography>
          {expanded && (
            <Box sx={{ ml: 'auto', display: 'inline-flex', alignItems: 'center' }}>
              <Tooltip title={sortMode === 'manual' ? 'Sort pasos' : `Sorted by ${sortMode === 'time' ? 'time' : 'WSJF'}`}>
                <IconButton
                  component="div"
                  role="button"
                  tabIndex={0}
                  size="small"
                  onClick={handleOpenSortMenu}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      e.stopPropagation();
                      setSortMenuAnchor(e.currentTarget as HTMLElement);
                    }
                  }}
                  onFocus={(e) => e.stopPropagation()}
                  sx={{ color: sortMode === 'manual' ? 'text.secondary' : 'primary.main' }}
                  aria-label="Sort pasos"
                >
                  <SortIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Menu
                anchorEl={sortMenuAnchor}
                open={Boolean(sortMenuAnchor)}
                onClose={handleCloseSortMenu}
                onClick={(e) => e.stopPropagation()}
              >
                <MenuItem selected={sortMode === 'manual'} onClick={() => handleSelectSortMode('manual')}>
                  {sortMode === 'manual' ? <CheckIcon fontSize="small" sx={{ mr: 1 }} /> : <Box sx={{ width: 20, mr: 1 }} />}
                  Drag Order (Default)
                </MenuItem>
                <MenuItem selected={sortMode === 'time'} onClick={() => handleSelectSortMode('time')}>
                  {sortMode === 'time' ? <CheckIcon fontSize="small" sx={{ mr: 1 }} /> : <Box sx={{ width: 20, mr: 1 }} />}
                  Time Order
                </MenuItem>
                <MenuItem selected={sortMode === 'wsjf'} onClick={() => handleSelectSortMode('wsjf')}>
                  {sortMode === 'wsjf' ? <CheckIcon fontSize="small" sx={{ mr: 1 }} /> : <Box sx={{ width: 20, mr: 1 }} />}
                  WSJF Order
                </MenuItem>
              </Menu>
            </Box>
          )}
        </Box>
      </AccordionSummary>
      {expanded && showWarning && (
        <AccordionDetails sx={{ pt: 2, pb: 0 }}>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Too much on your plate - reprioritize, say no, or delegate!
          </Alert>
        </AccordionDetails>
      )}
      {expanded && (
        <Droppable droppableId={checklistType}>
          {(provided, snapshot) => (
            <List
              ref={(el: HTMLUListElement | null) => {
                provided.innerRef(el);
                (listRef as React.MutableRefObject<HTMLElement | null>).current = el;
              }}
              {...provided.droppableProps}
              sx={{
                minHeight: 50,
                backgroundColor: snapshot.isDraggingOver ? 'action.hover' : 'transparent',
                transition: 'background-color 0.2s',
                borderRadius: 1,
                p: 1,
              }}
            >
              {(() => {
                let draggableIndex = 0;
                return visibleRenderEntries.map((entry) => {
                  if (entry.kind === 'group') {
                    const indentPx = entry.depth * 20;
                    return (
                      <ListItem
                        key={`group-${entry.id}`}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          mb: 0.5,
                          ml: { xs: 0, sm: `${indentPx}px` },
                          width: { xs: '100%', sm: `calc(100% - ${indentPx}px)` },
                          pl: { xs: `${Math.min(indentPx + 6, 52)}px`, sm: 0.75 },
                          boxSizing: 'border-box',
                          p: 0.75,
                          borderLeft: '2px solid #32CA1D',
                          borderRadius: 1,
                          backgroundColor: 'action.hover',
                        }}
                      >
                        {entry.isExpandableGroup ? (
                          <IconButton size="small" onClick={() => toggleGroupExpansion(entry.id)}>
                            {entry.expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                          </IconButton>
                        ) : (
                          <Box sx={{ width: 32, height: 32 }} />
                        )}
                        {entry.wsjfCost !== undefined || entry.wsjfValue !== undefined ? (
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
                            {entry.wsjfCost
                              ? <PriorityChipLabel kind="cost" value={entry.wsjfCost} />
                              : <UnknownPriorityChipLabel kind="cost" />}
                            <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500, mx: 0.1 }}>
                              /
                            </Typography>
                            {entry.wsjfValue
                              ? <PriorityChipLabel kind="value" value={entry.wsjfValue} />
                              : <UnknownPriorityChipLabel kind="value" />}
                          </Box>
                        ) : (
                          <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                            {entry.label}
                          </Typography>
                        )}
                        <Chip
                          label={`${entry.itemCount} paso${entry.itemCount === 1 ? '' : 's'}`}
                          size="small"
                          variant="outlined"
                        />
                      </ListItem>
                    );
                  }

                  const item = entry.item!;
                  const currentIndex = draggableIndex;
                  const indentPx = entry.depth * 20;
                  draggableIndex += 1;

                  return (
                    <Draggable key={item.id} draggableId={item.id} index={currentIndex} isDragDisabled={sortMode !== 'manual'}>
                      {(provided, snapshot) => (
                        <ListItem
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          {...(sortMode === 'manual' ? provided.dragHandleProps : {})}
                          data-paso-item-row="true"
                          style={getConstrainedDragStyle(provided.draggableProps.style, snapshot.isDragging)}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            mb: 0.5,
                            ml: { xs: 0, sm: `${indentPx}px` },
                            width: { xs: '100%', sm: `calc(100% - ${indentPx}px)` },
                            pl: { xs: `${Math.min(indentPx + 8, 54)}px`, sm: 1 },
                            boxSizing: 'border-box',
                            p: 1,
                            backgroundColor: snapshot.isDragging ? 'action.selected' : getItemBackgroundColor(item.customColor),
                            border: '1px solid',
                            borderColor: 'divider',
                            borderRadius: 1,
                            cursor: editingItemId === item.id ? 'default' : 'pointer',
                            borderLeft: isMultiLine(item.text) ? '3px solid #32CA1D' : '1px solid',
                            borderLeftColor: isMultiLine(item.text) ? '#32CA1D' : 'divider',
                            '&:hover': item.customColor
                              ? { opacity: editingItemId === item.id ? 1 : 0.8 }
                              : {
                                  backgroundColor: editingItemId === item.id ? 'background.paper' : 'action.hover',
                                },
                          }}
                          onClick={(e) => handleItemClick(e, item.id, item.customColor)}
                          onContextMenu={(e) => handleContextMenu(e, item.id)}
                          onTouchStart={(e) => handleItemTouchStart(e, item.id)}
                          onTouchMove={handleItemTouchMove}
                          onTouchEnd={clearLongPressState}
                          onTouchCancel={clearLongPressState}
                        >
                          <Checkbox
                            checked={false}
                            onChange={(e) => onCheckItem(item.id, e.target.checked)}
                            size="small"
                            onClick={(e) => e.stopPropagation()}
                          />
                          <>
                              <Box sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1, gap: 0.5 }}>
                                <MarkdownText>{getDisplayText(item.text)}</MarkdownText>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                  {checklistType === 'escalated' && (
                                    <Tooltip title="Escalated 📈">
                                      <Chip size="small" label="📈" />
                                    </Tooltip>
                                  )}
                                  {getCategoryChips(item, entry.groupedContext).map(category => (
                                    <Chip key={`${item.id}-${category}`} size="small" label={category.replace(/:/g, ' > ')} variant="outlined" />
                                  ))}
                                  {item.tags?.special?.estimatedTimeToDoneMinutes !== undefined && (
                                    <Chip size="small" label={<TimeChipLabel minutes={item.tags.special.estimatedTimeToDoneMinutes} />} />
                                  )}
                                  {item.tags?.special?.dueDate && (
                                    <Tooltip title="Paso-Upcoming linkage to arrive at future date">
                                      <Chip size="small" label={<DueDateChipLabel dueDate={item.tags.special.dueDate} />} />
                                    </Tooltip>
                                  )}
                                  {sortMode === 'wsjf' && hasUnknownWsjfPriority(item, 'cost') && (
                                    <Chip size="small" label={<UnknownPriorityChipLabel kind="cost" />} />
                                  )}
                                  {item.tags?.special?.opportunityCostIfNotDone && (
                                    <Chip size="small" label={<PriorityChipLabel kind="cost" value={item.tags.special.opportunityCostIfNotDone} />} />
                                  )}
                                  {sortMode === 'wsjf' && hasUnknownWsjfPriority(item, 'value') && (
                                    <Chip size="small" label={<UnknownPriorityChipLabel kind="value" />} />
                                  )}
                                  {item.tags?.special?.valueIfDone && (
                                    <Chip size="small" label={<PriorityChipLabel kind="value" value={item.tags.special.valueIfDone} />} />
                                  )}
                                </Box>
                              </Box>
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleStartEdit(item.id, item.text);
                                }}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onDeleteItem(item.id);
                                }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </>
                        </ListItem>
                      )}
                    </Draggable>
                  );
                });
              })()}
              {shouldShowOverflowDots && (
                <ListItem
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    p: 0.5,
                    my: 0.5,
                  }}
                >
                  <Tooltip title="Show all" enterTouchDelay={500} leaveTouchDelay={1800}>
                    <IconButton
                      size="small"
                      aria-label="Show all pasos"
                      onClick={(event) => {
                        event.stopPropagation();
                        setShowAllItems(true);
                        onShowAllPasos?.(title);
                      }}
                    >
                      <MoreVertIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </ListItem>
              )}
                {provided.placeholder}
              </List>
            )}
          </Droppable>
        )}

          {expanded && (
            <AccordionDetails sx={{ pt: 0 }}>
              {showAddInput ? (
                <Box ref={addFormRef} sx={{ display: 'flex', gap: 1, mt: 1, flexDirection: { xs: 'column', sm: 'row' } }}>
                  <TextField
                    fullWidth
                    size="small"
                    multiline
                    maxRows={10}
                    placeholder={
                      isMultiAddMode
                        ? 'Enter items... (Empty lines separate items)'
                        : 'Enter item text... (Shift+Enter for new line)'
                    }
                    value={newItemText}
                    onChange={(e) => setNewItemText(e.target.value)}
                    onKeyDown={handleKeyPress}
                    onBlur={handleAddTextFieldBlur}
                    autoFocus
                  />
                  <IconButton color="primary" onClick={() => { void handleAddItem(); }} disabled={!newItemText.trim()} sx={{ alignSelf: { xs: 'flex-end', sm: 'center' } }}>
                    <AddIcon />
                  </IconButton>
                </Box>
              ) : (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
                  <IconButton
                    color="primary"
                    onMouseEnter={handleAddButtonHover}
                    onMouseMove={handleAddButtonHover}
                    onMouseLeave={() => setShowMultiAddHint(false)}
                    onClick={handleOpenAddInput}
                    sx={{
                      border: '1px dashed',
                      borderColor: 'primary.main',
                      borderRadius: 1,
                      width: '100%',
                    }}
                  >
                    <AddIcon />
                    <Typography variant="body2" sx={{ ml: 1 }}>
                      {showMultiAddHint ? 'Add Multiple Items' : 'Add Item'}
                    </Typography>
                  </IconButton>
                </Box>
              )}
            </AccordionDetails>
          )}

      {/* Right-click context menu */}
      <ContextMenu
        position={contextMenu}
        onClose={handleCloseContextMenu}
      >
        <MenuItem onClick={handleMoveClick}>
          <ListItemIcon>
            <MoveIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Move</ListItemText>
          <ArrowRightIcon fontSize="small" sx={{ ml: 1 }} />
        </MenuItem>
      </ContextMenu>

      {/* Move submenu - list of target checklists */}
      <Menu
        open={Boolean(moveSubmenuAnchor)}
        onClose={handleCloseContextMenu}
        anchorEl={moveSubmenuAnchor}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        {allChecklistConfigs
          .filter(config => config.type !== checklistType)
          .map(config => (
            <MenuItem
              key={config.type}
              onClick={() => handleMoveToChecklist(config.type)}
            >
              {config.title}
            </MenuItem>
          ))}
      </Menu>

      <ColorSwatchModal
        open={Boolean(colorModalAnchor)}
        anchorEl={colorModalAnchor}
        onClose={handleCloseColorModal}
        onColorSelect={handleColorSelect}
        currentColor={selectedItemColor}
      />

      <GenericModal
        open={Boolean(editingItemId)}
        onClose={handleCancelEdit}
        ariaLabelledBy="paso-edit-item-modal"
      >
        <Box ref={editFormRef} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Typography id="paso-edit-item-modal" variant="h6" sx={{ fontWeight: 600 }}>
            Edit Paso
          </Typography>

          {currentEditingItem && (
            <>
              <TextField
                fullWidth
                size="small"
                multiline
                minRows={isMultiLine(currentEditingItem.text) ? 2 : 1}
                maxRows={10}
                value={editText}
                onChange={(e) => setEditText(e.target.value)}
                onKeyDown={(e) => handleEditKeyPress(e, currentEditingItem.id)}
                onBlur={(e) => handleEditTextFieldBlur(e, currentEditingItem.id)}
                autoFocus
                sx={{ flexGrow: 1 }}
                onClick={(e) => e.stopPropagation()}
                placeholder="Shift+Enter for new line, Enter to save"
              />

              <Divider />
              <Stack spacing={1}>
                <TextField
                  size="small"
                  label="Categories"
                  InputLabelProps={{ shrink: true }}
                  placeholder={isTagCategoriesFocused ? 'Fruits, Fruits:Berries' : undefined}
                  value={tagCategoriesInput}
                  onChange={(e) => setTagCategoriesInput(e.target.value)}
                  onFocus={() => setIsTagCategoriesFocused(true)}
                  onBlur={() => setIsTagCategoriesFocused(false)}
                  multiline
                  minRows={1}
                  fullWidth
                />
                <Box sx={{ display: 'flex', gap: 1, flexDirection: { xs: 'column', sm: 'row' } }}>
                  <TextField
                    size="small"
                    label="Est time"
                    InputLabelProps={{ shrink: true }}
                    type={isHuge ? 'text' : 'number'}
                    value={isHuge ? '—' : estimatedTimeValue}
                    onChange={(e) => setEstimatedTimeValue(e.target.value)}
                    disabled={isHuge}
                    sx={{
                      flex: 1,
                      '& input[type=number]': {
                        MozAppearance: 'textfield',
                      },
                      '& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button': {
                        WebkitAppearance: 'none',
                        margin: 0,
                      },
                    }}
                  />
                  <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 120 } }}>
                    <InputLabel id={`time-unit-${currentEditingItem.id}`} shrink>Unit</InputLabel>
                    <Select
                      labelId={`time-unit-${currentEditingItem.id}`}
                      value={estimatedTimeUnit}
                      displayEmpty
                      label="Unit"
                      disabled={isHuge}
                      renderValue={(selected) => selected ? selected : <NullSelectSymbol />}
                      onChange={(e: SelectChangeEvent) => setEstimatedTimeUnit(e.target.value as '' | 'minutes' | 'hours')}
                    >
                      <MenuItem value="">
                        <NullSelectSymbol />
                      </MenuItem>
                      <MenuItem value="minutes">Minutes</MenuItem>
                      <MenuItem value="hours">Hours</MenuItem>
                    </Select>
                  </FormControl>
                  <FormControlLabel
                    sx={{
                      ml: 0,
                      mr: 0,
                      height: 40,
                      minHeight: 40,
                      alignSelf: { xs: 'flex-start', sm: 'flex-end' },
                      '& .MuiFormControlLabel-label': {
                        display: 'inline-flex',
                        alignItems: 'center',
                      },
                    }}
                    control={
                      <Checkbox
                        size="small"
                        sx={{
                          color: AIRMASK_GREEN,
                          '&.Mui-checked': {
                            color: AIRMASK_GREEN,
                          },
                        }}
                        checked={isHuge}
                        onChange={(e) => {
                          const nextIsHuge = e.target.checked;
                          setIsHuge(nextIsHuge);
                          if (nextIsHuge) {
                            setEstimatedTimeValue('');
                            setEstimatedTimeUnit('');
                            return;
                          }
                          if (estimatedTimeUnit === '') {
                            setEstimatedTimeUnit('minutes');
                          }
                        }}
                      />
                    }
                    label={
                      <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography variant="body2">Is Huge?</Typography>
                        <Tooltip title="Too large to estimate">
                          <InfoOutlinedIcon sx={{ fontSize: 15, color: 'text.secondary' }} />
                        </Tooltip>
                      </Box>
                    }
                  />
                </Box>
                <TextField
                  size="small"
                  label="Do by"
                  InputLabelProps={{ shrink: true }}
                  type="date"
                  value={dueDateValue}
                  onChange={(e) => setDueDateValue(e.target.value)}
                  onMouseDown={(e) => {
                    const input = e.target as HTMLInputElement & { showPicker?: () => void };
                    if (!input.showPicker) return;
                    try {
                      input.showPicker();
                    } catch {
                      // Some browsers reject showPicker despite pointer gestures.
                    }
                  }}
                  fullWidth
                  sx={{
                    '& input[type="date"]::-webkit-calendar-picker-indicator': {
                      display: 'none',
                      WebkitAppearance: 'none',
                    },
                    '& input[type="date"]::-webkit-datetime-edit': {
                      color: dueDateValue ? 'inherit' : 'transparent',
                    },
                    '& input[type="date"]:focus::-webkit-datetime-edit': {
                      color: 'inherit',
                    },
                  }}
                />
                <FormControl size="small" fullWidth>
                  <InputLabel
                    id={`cost-${currentEditingItem.id}`}
                    shrink
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 0.5,
                      pointerEvents: 'auto',
                      color: 'text.secondary',
                      backgroundColor: 'background.paper',
                      pr: 0.5,
                    }}
                  >
                    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, color: 'inherit' }}>
                      Neglect cost
                      <Tooltip title="Cost if not done">
                        <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center' }}>
                          <InfoOutlinedIcon sx={{ fontSize: 15, color: 'inherit' }} />
                        </Box>
                      </Tooltip>
                    </Box>
                  </InputLabel>
                  <Select
                    labelId={`cost-${currentEditingItem.id}`}
                    value={opportunityCostIfNotDone}
                    displayEmpty
                    label="Neglect cost"
                    renderValue={(selected) => selected
                      ? <PrioritySelectValue value={selected as PriorityLevel} reserveDropdownArrowSpace />
                      : <NullSelectSymbol />}
                    onChange={(e: SelectChangeEvent) => setOpportunityCostIfNotDone(e.target.value as '' | 'high' | 'moderate' | 'low')}
                    aria-label="Neglect cost"
                  >
                    <MenuItem value="">
                      <NullSelectSymbol />
                    </MenuItem>
                    <MenuItem value="high">
                      <PriorityMenuOption value="high" />
                    </MenuItem>
                    <MenuItem value="moderate">
                      <PriorityMenuOption value="moderate" />
                    </MenuItem>
                    <MenuItem value="low">
                      <PriorityMenuOption value="low" />
                    </MenuItem>
                  </Select>
                </FormControl>
                <FormControl size="small" fullWidth>
                  <InputLabel id={`value-${currentEditingItem.id}`} shrink>Value if done</InputLabel>
                  <Select
                    labelId={`value-${currentEditingItem.id}`}
                    value={valueIfDone}
                    displayEmpty
                    label="Value if done"
                    renderValue={(selected) => selected
                      ? <PrioritySelectValue value={selected as PriorityLevel} reserveDropdownArrowSpace />
                      : <NullSelectSymbol />}
                    onChange={(e: SelectChangeEvent) => setValueIfDone(e.target.value as '' | 'high' | 'moderate' | 'low')}
                    aria-label="Value if done"
                  >
                    <MenuItem value="">
                      <NullSelectSymbol />
                    </MenuItem>
                    <MenuItem value="high">
                      <PriorityMenuOption value="high" />
                    </MenuItem>
                    <MenuItem value="moderate">
                      <PriorityMenuOption value="moderate" />
                    </MenuItem>
                    <MenuItem value="low">
                      <PriorityMenuOption value="low" />
                    </MenuItem>
                  </Select>
                </FormControl>
              </Stack>

              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                <SaveActionButton
                  onClick={() => handleSaveEdit(currentEditingItem.id)}
                  disabled={!editText.trim()}
                  text=""
                  ariaLabel="Save"
                  size="small"
                />
              </Box>
            </>
          )}
        </Box>
      </GenericModal>
    </Accordion>
  );
};

export default ChecklistExpand;
